import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, RefreshCw, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";

const TIMEOUT_MS = 15000;

const pesanError = (e) => {
  const n = e?.name; const m = String(e?.message || "");
  if (n === "NotAllowedError" || n === "SecurityError") return "Izin kamera ditolak. Klik ikon kamera/gembok di address bar browser, izinkan akses kamera, lalu coba lagi.";
  if (n === "NotFoundError" || n === "DevicesNotFoundError") return "Kamera tidak ditemukan. Pastikan kamera USB terhubung dan dikenali komputer.";
  if (n === "NotReadableError" || n === "TrackStartError" || n === "AbortError" || /timeout starting video source/i.test(m)) return "Kamera gagal dinyalakan (Timeout starting video source). Cabut lalu pasang kembali kabel USB kamera, pilih kamera lain dari daftar, atau tutup aplikasi lain yang memakai kamera, kemudian klik Coba Lagi.";
  if (n === "OverconstrainedError") return "Kamera yang dipilih tidak tersedia. Pilih kamera lain dari daftar.";
  if (!window.isSecureContext) return "Akses kamera hanya berfungsi melalui HTTPS atau localhost.";
  return m || "Gagal mengakses kamera.";
};

const withTimeout = (p, ms) => new Promise((res, rej) => {
  const t = setTimeout(() => rej(Object.assign(new Error("Timeout starting video source"), { name: "AbortError" })), ms);
  p.then((v) => { clearTimeout(t); res(v); }, (e) => { clearTimeout(t); rej(e); });
});

// Ambil foto dari kamera USB/webcam via getUserMedia; hasil dikirim sebagai File (JPEG)
export const KameraFotoDialog = ({ open, onClose, onAmbil }) => {
  const streamRef = useRef(null); const reqRef = useRef(0);
  const [videoEl, setVideoEl] = useState(null);
  const [hasil, setHasil] = useState(null); const [err, setErr] = useState(null); const [loading, setLoading] = useState(false); const [siap, setSiap] = useState(false);
  const [devices, setDevices] = useState([]); const [deviceId, setDeviceId] = useState("");

  const stop = useCallback(() => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; }, []);

  const buka = useCallback(async (id, el) => {
    const v = el; if (!v) return;
    const req = ++reqRef.current;
    stop(); v.srcObject = null; setErr(null); setSiap(false); setLoading(true);
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("Browser tidak mendukung akses kamera. Gunakan Chrome/Edge/Firefox terbaru.");
      let list = (await navigator.mediaDevices.enumerateDevices()).filter((x) => x.kind === "videoinput");
      let target = id || (list.length === 1 ? list[0].deviceId : "");
      let s;
      try { s = await withTimeout(navigator.mediaDevices.getUserMedia({ video: target ? { deviceId: { exact: target } } : true, audio: false }), TIMEOUT_MS); }
      catch (e) {
        if (target && e?.name !== "NotAllowedError" && e?.name !== "SecurityError") { stop(); s = await withTimeout(navigator.mediaDevices.getUserMedia({ video: true, audio: false }), TIMEOUT_MS); }
        else throw e;
      }
      if (req !== reqRef.current) { s.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = s;
      list = (await navigator.mediaDevices.enumerateDevices()).filter((x) => x.kind === "videoinput");
      setDevices(list);
      const aktif = s.getVideoTracks()[0]?.getSettings?.().deviceId; setDeviceId(aktif || target || "");
      v.srcObject = s;
      await withTimeout(new Promise((res) => { if (v.readyState >= 1) res(); else v.onloadedmetadata = () => res(); }), TIMEOUT_MS);
      await v.play().catch(() => {});
      if (req === reqRef.current) setSiap(true);
    } catch (e) { if (req === reqRef.current) { stop(); setErr(pesanError(e)); } }
    finally { if (req === reqRef.current) setLoading(false); }
  }, [stop]);

  // Mulai hanya setelah <video> benar-benar ada di DOM
  useEffect(() => {
    if (!open) { reqRef.current++; stop(); setHasil(null); setErr(null); setSiap(false); setDevices([]); setDeviceId(""); return; }
    if (videoEl && !hasil) buka(deviceId, videoEl);
    return () => { reqRef.current++; stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, videoEl]);

  const ambil = () => {
    const v = videoEl; if (!v || !v.videoWidth) return toast.error("Kamera belum siap, tunggu sebentar");
    const side = Math.min(v.videoWidth, v.videoHeight); const c = document.createElement("canvas"); c.width = c.height = Math.min(side, 1024);
    c.getContext("2d").drawImage(v, (v.videoWidth - side) / 2, (v.videoHeight - side) / 2, side, side, 0, 0, c.width, c.height);
    setHasil(c.toDataURL("image/jpeg", 0.92)); stop(); setSiap(false);
  };
  const gunakan = async () => { const blob = await (await fetch(hasil)).blob(); onAmbil(new File([blob], `kamera-${Date.now()}.jpg`, { type: "image/jpeg" })); onClose(); };
  const ulang = () => { setHasil(null); setVideoEl(null); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="kamera-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Camera className="h-5 w-5" /> Ambil Foto dari Kamera</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="relative mx-auto w-full aspect-square max-w-[420px] rounded-2xl overflow-hidden bg-black">
            {hasil ? <img src={hasil} alt="Hasil foto" className="h-full w-full object-cover" data-testid="kamera-hasil" /> : (<>
              <video ref={setVideoEl} autoPlay playsInline muted className="h-full w-full object-cover" data-testid="kamera-video" />
              {err ? <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-red-200 bg-black/80" data-testid="kamera-error">{err}</div> : (<>
                {(loading || !siap) && <div className="absolute inset-0 flex items-center justify-center gap-2 text-white text-sm bg-black/60" data-testid="kamera-loading"><Loader2 className="h-5 w-5 animate-spin" /> Menghubungkan kamera (maks. 15 detik)...</div>}
                <div className="absolute inset-0 pointer-events-none"><div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[52%] h-[72%] rounded-[50%] border-4 border-white/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" data-testid="kamera-panduan" /></div>
                <div className="absolute bottom-3 inset-x-0 text-center text-white text-sm font-medium drop-shadow">Posisikan wajah di dalam kotak</div></>)}
            </>)}
          </div>
          {!hasil && devices.length > 1 && (
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Pilih kamera</label>
              <select value={deviceId} onChange={(e) => buka(e.target.value, videoEl)} disabled={loading} className="w-full min-h-[40px] rounded-xl border border-input bg-background px-3 text-sm" data-testid="kamera-pilih">
                {devices.map((x, i) => <option key={x.deviceId} value={x.deviceId}>{x.label || `Kamera ${i + 1}`}</option>)}
              </select>
            </div>)}
          <div className="flex gap-2 justify-end">
            {hasil ? (<><Button variant="outline" className="rounded-xl min-h-[44px]" onClick={ulang} data-testid="kamera-ulang"><RefreshCw className="h-4 w-4 mr-2" />Ambil Ulang</Button><Button className="rounded-xl min-h-[44px]" onClick={gunakan} data-testid="kamera-gunakan"><Check className="h-4 w-4 mr-2" />Gunakan Foto Ini</Button></>)
              : (<><Button variant="outline" className="rounded-xl min-h-[44px]" onClick={onClose} data-testid="kamera-batal"><X className="h-4 w-4 mr-2" />Batal</Button>
                {err ? <Button className="rounded-xl min-h-[44px]" onClick={() => buka("", videoEl)} data-testid="kamera-coba-lagi"><RefreshCw className="h-4 w-4 mr-2" />Coba Lagi</Button>
                  : <Button className="rounded-xl min-h-[44px]" onClick={ambil} disabled={!siap} data-testid="kamera-ambil"><Camera className="h-4 w-4 mr-2" />Ambil Gambar</Button>}</>)}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
